import './LoginWithEmail.css'
import { IonButton,IonContent,IonHeader, IonInput, IonPage, IonSpinner, IonIcon, useIonAlert } from "@ionic/react"
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { logInOutline, informationCircleOutline } from 'ionicons/icons';
import AppHeader from "../../../components/AppHeader/AppHeader";
import { useAppDispatch, useAppSelector } from "../../../services/redux/hook";
import { UserInfo, WalletRecoveryEmailRequestParameters } from "../../../types/types";
import { setUser } from "../../../services/redux/slices/user/user";
import { ebioroApi } from '../../../services/api/ebioro/ebioro';


const LoginWithEmail: React.FC<any> = () => {

    const history = useHistory();
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();
    const user: UserInfo = useAppSelector((state) => state.user);
    const [isSpinner, setIsSpinner] = useState(false);

    const [present] = useIonAlert();

    const hiddenButtonRef = useRef<HTMLIonButtonElement>(null); 

    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error.data}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        return;
                    }
                }
            ],

        });

    }

    const {
        handleSubmit,
        trigger, // Trigger validation manually
        setValue,
        register,
        getValues,
        watch,
        formState: { isValid, errors }
    } = useForm({
        mode: "onChange", // Validate form on change
        defaultValues: { email: '' }
    });

    const emailValue = watch('email');

    const onSubmit = async () => {

        const data = getValues();

        setIsSpinner(true);

        const updatedUser = { ...user }

        updatedUser.email = data.email;
        dispatch(setUser(updatedUser));

        try {
            // First, check if keys exist and migration status
            const migrationData = await dispatch(ebioroApi.endpoints.checkKeysMigration.initiate({ email: data.email })).unwrap();

            if (migrationData.has_keys && !migrationData.keys_migrated) {
                setIsSpinner(false);
                presentErrorAlert({ data: t('login.email.KEYS_NOT_MIGRATED')});
                return;
            }
                   
           
            const params: WalletRecoveryEmailRequestParameters = {
                email: data.email
            }

            const response = await dispatch(ebioroApi.endpoints.recoverWalletByEmail.initiate(params)).unwrap();

            setIsSpinner(false)

            history.push({
                pathname: '/login/email/verify',
                state: {
                    detail: {
                        token: response.partial_token,
                    }
                }
            });

        } catch (error: any) {

            console.log('Error during email login process:', error);

            setIsSpinner(false);

            // Handle checkKeysMigration errors
            if (error.status === 404 && error.error) {
                presentErrorAlert({ data: error.error });
            } else if (error.data && error.status === 404) {
                // Handle recoverWalletByEmail errors
                presentErrorAlert({ data: error.data.error });
            } else {
                presentErrorAlert({ data: t('application.uncaught.ERROR') });
            }

        }
    }

    return (
        <IonPage>

            <IonHeader className="ion-no-border">
                <AppHeader
                    title={t('login.email.page.HEADER') as string}
                    goBack={true}
                />
            </IonHeader>

            <IonContent className="recovery-email-content">
                {/* Hero Section */}
                <div className="hero-section">
                    <div className="hero-content">
                        <div className="icon-wrapper">
                            <IonIcon 
                                icon={logInOutline} 
                                className="hero-icon"
                            />
                        </div>
                       
                        <p className="hero-description">
                            {t('login.email.page.SUBTITLE')}
                        </p>
                    </div>
                </div>

                {/* Form Section */}
                <div className="form-section">
                    <form onSubmit={handleSubmit(onSubmit)} className="recovery-form">
                        <div className="input-container">
                            <IonInput
                                label={t('login.email.page.EMAIL_LABEL') as string}
                                placeholder={t('login.email.page.EMAIL_PLACEHOLDER') as string}
                                className="email-input"
                                labelPlacement="floating"
                                mode="md"
                                fill="outline"
                                type="email"
                                onIonInput={(e: CustomEvent) => {
                                    setValue('email', e.detail.value!);
                                    trigger('email');
                                }}
                                {...register('email', {
                                    required: `${t('registration.email.ERROR_REQUIRED')}`,
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                        message: `${t('registration.email.ERROR_INVALID')}`
                                    }
                                })} 
                            />
                        </div>

                        {/* Help Text */}
                        <div className="help-notice">
                            <div className="help-content">
                                <IonIcon icon={informationCircleOutline} className="help-icon" />
                                <p className="help-text">
                                    {t('login.email.page.HELP_TEXT')}
                                </p>
                            </div>
                        </div>

                        {/* Hidden submit button */}
                        <IonButton type="submit" ref={hiddenButtonRef} style={{ display: 'none' }} />
                    </form>
                </div>

                {/* Button Section - Inside Content */}
                <div className="button-section">
                    <IonButton
                        color='primary'
                        onClick={() => {
                            if (isValid && !isSpinner) {
                                hiddenButtonRef.current?.click();
                            }
                        }}
                        expand="block"
                        className="continue-button"
                        disabled={!emailValue || !isValid || isSpinner}
                    >
                        {isSpinner ? (
                            <IonSpinner className="BUTTON-SPINNER" />
                        ) : (
                            t('login.email.page.CONTINUE_BUTTON')
                        )}
                    </IonButton>
                </div>
                
            </IonContent>

        </IonPage>
    );
}

export default LoginWithEmail;
